import React, { useState } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Box,
    Typography,
    Switch,
    TextField,
    Button,
    Grid2,
    FormControlLabel,
    Alert,
    Divider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip
} from '@mui/material';
import {
    Settings as SettingsIcon,
    Save as SaveIcon,
    AutoMode as AutoModeIcon
} from '@mui/icons-material';
import { PositionHealth, PoolHealth } from '../../types/DashboardTypes';

interface AutoCompoundConfigCardProps {
    dexType: 'uniswap' | 'aerodrome';
    contractAddress?: string;
    positions?: PositionHealth[];
    pools?: PoolHealth[];
}

const AutoCompoundConfigCard: React.FC<AutoCompoundConfigCardProps> = ({
    dexType,
    contractAddress,
    positions = [],
    pools = []
}) => {
    const [config, setConfig] = useState({
        enabled: false,
        minFeeThresholdETH: '0.01',
        gasMultiplier: '3',
        minInterval: '24', // hours
        selectedTarget: '', // tokenId for Uniswap, pool address for Aerodrome
    });

    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const handleConfigChange = (field: string, value: string | boolean) => {
        setConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveConfig = async () => {
        setIsSaving(true);
        setSaveMessage(null);

        try {
            // TODO: Call contract function based on DEX type
            if (dexType === 'uniswap') {
                // await uniswapContract.enableAutoCompounding(
                //     config.selectedTarget,
                //     ethers.utils.parseEther(config.minFeeThresholdETH),
                //     config.gasMultiplier,
                //     parseInt(config.minInterval) * 3600 // Convert hours to seconds
                // );
                console.log('Enabling Uniswap auto-compound:', config);
            } else {
                // await aerodromeContract.enableAutoCompounding(
                //     config.selectedTarget,
                //     ethers.utils.parseEther(config.minFeeThresholdETH),
                //     config.gasMultiplier,
                //     parseInt(config.minInterval) * 3600
                // );
                console.log('Enabling Aerodrome auto-compound:', config);
            }

            // Simulate transaction
            await new Promise(resolve => setTimeout(resolve, 2000));

            setSaveMessage({ type: 'success', text: 'Auto-compound configuration saved successfully!' });
        } catch (error) {
            console.error('Failed to save configuration:', error);
            setSaveMessage({ type: 'error', text: 'Failed to save configuration. Please try again.' });
        } finally {
            setIsSaving(false);
        }
    };

    const getTargetOptions = () => {
        if (dexType === 'uniswap') {
            return positions.map(pos => ({
                value: pos.tokenId,
                label: `Position #${pos.tokenId} (${pos.inRange ? 'In Range' : 'Out of Range'})`
            }));
        } else {
            return pools.map(pool => ({
                value: pool.pool,
                label: `${pool.token0Symbol}-${pool.token1Symbol} ${pool.isStable ? '(Stable)' : '(Volatile)'}`
            }));
        }
    };

    const targetOptions = getTargetOptions();

    const validateConfig = () => {
        const threshold = parseFloat(config.minFeeThresholdETH);
        const multiplier = parseInt(config.gasMultiplier);
        const interval = parseInt(config.minInterval);

        return threshold > 0 &&
            multiplier >= 2 &&
            interval >= 1 &&
            config.selectedTarget !== '';
    };

    const isConfigValid = validateConfig();

    return (
        <Card elevation={3}>
            <CardHeader
                title={
                    <Box display="flex" alignItems="center" gap={1}>
                        <SettingsIcon color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                            Auto-Compound Configuration
                        </Typography>
                    </Box>
                }
                subheader={`Configure automatic fee compounding for ${dexType === 'uniswap' ? 'positions' : 'pools'}`}
            />
            <CardContent>
                <Grid2 container spacing={3}>
                    {/* Enable/Disable Toggle */}
                    <Grid2 size={{ xs: 12 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={config.enabled}
                                    onChange={(e) => handleConfigChange('enabled', e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <AutoModeIcon fontSize="small" />
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        Enable Automatic Fee Compounding
                                    </Typography>
                                </Box>
                            }
                        />
                        <Typography variant="body2" color="textSecondary" sx={{ ml: 4 }}>
                            Automatically collect and reinvest fees when economically viable
                        </Typography>
                    </Grid2>

                    {config.enabled && (
                        <>
                            <Grid2 size={{ xs: 12 }}>
                                <Divider />
                            </Grid2>

                            {/* Target Selection */}
                            <Grid2 size={{ xs: 12 }}>
                                <FormControl fullWidth>
                                    <InputLabel>
                                        {dexType === 'uniswap' ? 'Target Position' : 'Target Pool'}
                                    </InputLabel>
                                    <Select
                                        value={config.selectedTarget}
                                        onChange={(e) => handleConfigChange('selectedTarget', e.target.value)}
                                        label={dexType === 'uniswap' ? 'Target Position' : 'Target Pool'}
                                    >
                                        {targetOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid2>

                            {/* Configuration Parameters */}
                            <Grid2 size={{ xs: 12, md: 4 }}>
                                <TextField
                                    label="Minimum Fee Threshold (ETH)"
                                    value={config.minFeeThresholdETH}
                                    onChange={(e) => handleConfigChange('minFeeThresholdETH', e.target.value)}
                                    type="number"
                                    inputProps={{ step: "0.001", min: "0" }}
                                    fullWidth
                                    helperText="Minimum fee value to trigger compounding"
                                />
                            </Grid2>

                            <Grid2 size={{ xs: 12, md: 4 }}>
                                <TextField
                                    label="Gas Cost Multiplier"
                                    value={config.gasMultiplier}
                                    onChange={(e) => handleConfigChange('gasMultiplier', e.target.value)}
                                    type="number"
                                    inputProps={{ min: "2", max: "10" }}
                                    fullWidth
                                    helperText="Fees must be X times gas cost (recommended: 3)"
                                />
                            </Grid2>

                            <Grid2 size={{ xs: 12, md: 4 }}>
                                <TextField
                                    label="Minimum Interval (hours)"
                                    value={config.minInterval}
                                    onChange={(e) => handleConfigChange('minInterval', e.target.value)}
                                    type="number"
                                    inputProps={{ min: "1", max: "168" }}
                                    fullWidth
                                    helperText="Minimum time between compounds"
                                />
                            </Grid2>

                            {/* Configuration Summary */}
                            <Grid2 size={{ xs: 12 }}>
                                <Box sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                        📋 Configuration Summary
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={1}>
                                        <Chip
                                            label={`Min Fees: ${config.minFeeThresholdETH} ETH`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={`Gas Multiplier: ${config.gasMultiplier}x`}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={`Interval: ${config.minInterval}h`}
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                        />
                                    </Box>
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                        💡 Auto-compounding will only execute when fees are at least {config.gasMultiplier}x the gas cost
                                    </Typography>
                                </Box>
                            </Grid2>

                            {/* Save Button */}
                            <Grid2 size={{ xs: 12 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveConfig}
                                    disabled={!isConfigValid || isSaving}
                                    startIcon={<SaveIcon />}
                                    fullWidth
                                    size="large"
                                >
                                    {isSaving ? 'Saving Configuration...' : 'Save Auto-Compound Settings'}
                                </Button>
                            </Grid2>
                        </>
                    )}

                    {/* Save Message */}
                    {saveMessage && (
                        <Grid2 size={{ xs: 12 }}>
                            <Alert severity={saveMessage.type}>
                                {saveMessage.text}
                            </Alert>
                        </Grid2>
                    )}

                    {/* Information Box */}
                    <Grid2 size={{ xs: 12 }}>
                        <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                🤖 How Auto-Compounding Works
                            </Typography>
                            <Typography variant="body2">
                                • Anyone can trigger compounding by calling the public trigger function<br />
                                • Compounding only executes when fees exceed the gas cost threshold<br />
                                • Callers receive a small incentive (0.1% of fees) for triggering compounds<br />
                                • Time constraints prevent spam and ensure optimal timing
                            </Typography>
                        </Alert>
                    </Grid2>
                </Grid2>
            </CardContent>
        </Card>
    );
};

export default AutoCompoundConfigCard;
